// frontend/src/components/UserMenu.jsx
/**
 * UserMenu — top-right account menu in the header.
 *
 * @description
 *   Avatar IconButton that opens a tiny menu with the current user email
 *   and a Sign out item. Sign-out triggers Supabase auth.signOut() which
 *   AuthGate detects (session becomes null) and renders the full-screen
 *   Login. No manual redirect needed.
 *
 * @useWhen
 *   - Top-level header user controls (account email + sign out)
 *
 * @dontUseFor
 *   - Multi-account switching (single-user POG today; widen the menu when multi-user lands)
 *   - User settings page navigation (use a separate route)
 *
 * @related
 *   - AuthGate — redirects to Login when signOut clears the session
 *   - Bell — sibling in the header right cluster
 */
import { useState } from 'react'
import {
  IconButton, Menu, MenuItem, Typography, Tooltip, Divider, Box,
} from '@mui/material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import { useUser, useSupabaseClient } from '@supabase/auth-helpers-react'

export default function UserMenu() {
  const user = useUser()
  const supabase = useSupabaseClient()
  const [anchor, setAnchor] = useState(null)
  const open = Boolean(anchor)

  const handleSignOut = async () => {
    setAnchor(null)
    try {
      await supabase.auth.signOut()
    } catch {
      // even if signOut errors, AuthGate will catch any subsequent 401 + redirect
    }
  }

  if (!user) return null

  return (
    <>
      <Tooltip title={user.email || 'Account'}>
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          color="inherit"
          aria-label="Account menu"
        >
          <AccountCircleOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Signed in as
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {user.email || user.id}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
          Sign out
        </MenuItem>
      </Menu>
    </>
  )
}
